export type PomodoroPhase = 'focus' | 'break'
export type PomodoroStatus = 'idle' | 'running' | 'paused' | 'completed'

export interface PomodoroSettings {
  focusMinutes: number
  breakMinutes: number
  sound: boolean
  vibration: boolean
  notifications: boolean
}

export interface PomodoroState {
  phase: PomodoroPhase
  status: PomodoroStatus
  remainingMs: number
  deadlineMs: number | null
  completedFocusCycles: number
}

export interface PomodoroReconciliation {
  state: PomodoroState
  completedPhase: PomodoroPhase | null
}

export const POMODORO_SETTINGS_KEY = 'dateify.pomodoro.settings'
export const DEFAULT_POMODORO_SETTINGS: PomodoroSettings = {
  focusMinutes: 25,
  breakMinutes: 5,
  sound: false,
  vibration: false,
  notifications: false,
}

const SETTINGS_VERSION = 1
const MIN_DURATION_MINUTES = 1
const MAX_FOCUS_MINUTES = 180
const MAX_BREAK_MINUTES = 60

export function isValidPomodoroSettings(settings: PomodoroSettings): boolean {
  return isValidDuration(settings.focusMinutes, MAX_FOCUS_MINUTES) &&
    isValidDuration(settings.breakMinutes, MAX_BREAK_MINUTES)
}

export function createPomodoroState(
  settings: PomodoroSettings,
  completedFocusCycles = 0,
): PomodoroState {
  return {
    phase: 'focus',
    status: 'idle',
    remainingMs: durationForPhase('focus', settings),
    deadlineMs: null,
    completedFocusCycles: Math.max(0, Math.trunc(completedFocusCycles)),
  }
}

export function startPomodoro(state: PomodoroState, nowMs: number): PomodoroState {
  if (state.status === 'running' || state.remainingMs <= 0) return state

  return {
    ...state,
    status: 'running',
    deadlineMs: nowMs + state.remainingMs,
  }
}

export function pausePomodoro(state: PomodoroState, nowMs: number): PomodoroState {
  if (state.status !== 'running' || state.deadlineMs === null) return state

  return {
    ...state,
    status: 'paused',
    remainingMs: Math.max(0, state.deadlineMs - nowMs),
    deadlineMs: null,
  }
}

export function pauseOrCompletePomodoro(
  state: PomodoroState,
  settings: PomodoroSettings,
  nowMs: number,
): PomodoroReconciliation {
  const reconciled = reconcilePomodoro(state, settings, nowMs)
  if (reconciled.completedPhase) return reconciled

  return {
    state: pausePomodoro(reconciled.state, nowMs),
    completedPhase: null,
  }
}

export function reconcilePomodoro(
  state: PomodoroState,
  settings: PomodoroSettings,
  nowMs: number,
): PomodoroReconciliation {
  if (state.status !== 'running' || state.deadlineMs === null) {
    return { state, completedPhase: null }
  }

  const remainingMs = Math.max(0, state.deadlineMs - nowMs)
  if (remainingMs > 0) {
    return {
      state: { ...state, remainingMs },
      completedPhase: null,
    }
  }

  const completedPhase = state.phase
  const nextPhase: PomodoroPhase = completedPhase === 'focus' ? 'break' : 'focus'

  return {
    completedPhase,
    state: {
      phase: nextPhase,
      status: 'completed',
      remainingMs: durationForPhase(nextPhase, settings),
      deadlineMs: null,
      completedFocusCycles: state.completedFocusCycles + (completedPhase === 'focus' ? 1 : 0),
    },
  }
}

export function resetPomodoro(settings: PomodoroSettings): PomodoroState {
  return createPomodoroState(settings)
}

export function applyPomodoroSettings(
  state: PomodoroState,
  settings: PomodoroSettings,
): PomodoroState {
  if (state.status === 'running') return state

  return {
    ...state,
    status: 'idle',
    remainingMs: durationForPhase(state.phase, settings),
    deadlineMs: null,
  }
}

export function serializePomodoroSettings(settings: PomodoroSettings): string {
  return JSON.stringify({ version: SETTINGS_VERSION, settings })
}

export function parsePomodoroSettings(value: string | null): PomodoroSettings {
  if (!value) return DEFAULT_POMODORO_SETTINGS

  try {
    const parsed: unknown = JSON.parse(value)
    if (!isSettingsEnvelope(parsed)) return DEFAULT_POMODORO_SETTINGS
    if (!isValidPomodoroSettings(parsed.settings)) return DEFAULT_POMODORO_SETTINGS

    return parsed.settings
  } catch {
    return DEFAULT_POMODORO_SETTINGS
  }
}

export function loadPomodoroSettings(
  storage: Pick<Storage, 'getItem'>,
): PomodoroSettings {
  try {
    return parsePomodoroSettings(storage.getItem(POMODORO_SETTINGS_KEY))
  } catch {
    return DEFAULT_POMODORO_SETTINGS
  }
}

export function savePomodoroSettings(
  storage: Pick<Storage, 'setItem'>,
  settings: PomodoroSettings,
): boolean {
  try {
    storage.setItem(POMODORO_SETTINGS_KEY, serializePomodoroSettings(settings))
    return true
  } catch {
    return false
  }
}

function durationForPhase(phase: PomodoroPhase, settings: PomodoroSettings): number {
  const minutes = phase === 'focus' ? settings.focusMinutes : settings.breakMinutes
  return minutes * 60 * 1000
}

function isValidDuration(value: number, max: number): boolean {
  return Number.isInteger(value) && value >= MIN_DURATION_MINUTES && value <= max
}

function isSettingsEnvelope(value: unknown): value is {
  version: 1
  settings: PomodoroSettings
} {
  if (!value || typeof value !== 'object') return false
  const envelope = value as Record<string, unknown>
  if (envelope.version !== SETTINGS_VERSION) return false
  if (!envelope.settings || typeof envelope.settings !== 'object') return false

  const settings = envelope.settings as Record<string, unknown>
  return typeof settings.focusMinutes === 'number' &&
    typeof settings.breakMinutes === 'number' &&
    typeof settings.sound === 'boolean' &&
    typeof settings.vibration === 'boolean' &&
    typeof settings.notifications === 'boolean'
}
